import React, { useState, useEffect } from 'react';
import { gql } from '@apollo/client';

import { initialAccountContextState } from './constants';

export const AccountContext = React.createContext(initialAccountContextState);

export type planType = {
  id: string | null;
  isAdmin: boolean;
  isOneBufferOrganization: boolean;
};

function getPlanId(account): string {
  const currentOrganization = account?.account?.currentOrganization;
  if (currentOrganization && currentOrganization.isOneBufferOrganization) {
    return currentOrganization.billing.subscription.plan.id;
  }

  return '';
}

export function usePlan(): planType {
  const account = React.useContext(AccountContext);
  if (account === undefined) {
    throw new Error('useAccount must be used within a AccountContext');
  }

  const [plan, setPlan] = useState({
    id: '',
    isAdmin: false,
    isOneBufferOrganization: true,
  });

  useEffect(() => {
    const currentOrganization = account?.account?.currentOrganization;

    setPlan({
      id: getPlanId(account),
      isAdmin: currentOrganization?.role === 'admin',
      isOneBufferOrganization: currentOrganization?.isOneBufferOrganization,
    });
  }, [account]);

  return plan;
}

export const useAccount = () => {
  const context = React.useContext(AccountContext);
  if (context === undefined) {
    throw new Error('useAccount must be used within a AccountContext');
  }
  return context;
};

// Custom hook to keep track if the homepage has been initially loaded
export const usePageLoad = (): {
  initialPageLoad: boolean;
  updateInitialPageLoad: () => void;
} => {
  const [initialPageLoad, setInitialPageLoad] = useState(false);

  const updateInitialPageLoad = (): void => {
    setInitialPageLoad(true);
  };
  return { initialPageLoad, updateInitialPageLoad };
};

export const useDismissedBanners = () => {
  const account = React.useContext(AccountContext);
  if (account === undefined) {
    throw new Error('useAccount must be used within a AccountContext');
  }

  return account?.account?.dismissedBanners;
};

export const useChannelsConnected = () => {
  const account = React.useContext(AccountContext);
  if (account === undefined) {
    throw new Error('useAccount must be used within a AccountContext');
  }

  return account?.account?.currentOrganization?.channels?.map(
    (channel) => channel.service
  );
};

export const useOrganizationLimits = () => {
  const account = React.useContext(AccountContext);
  if (account === undefined) {
    throw new Error(
      'useOrganizationLimits must be used within an AccountContext'
    );
  }

  return account?.account?.currentOrganization?.limits;
};

export const useOrganizationBilling = () => {
  const account = React.useContext(AccountContext);
  if (account === undefined) {
    throw new Error(
      'useOrganizationBilling must be used within an AccountContext'
    );
  }

  return account?.account?.currentOrganization?.billing;
};

export const GET_ACCOUNT = gql`
  query {
    account {
      id
      featureFlips
      dismissedBanners
      currentOrganization {
        id
        isOneBufferOrganization
        createdAt
        channels {
          id
          name
          service
          organizationId
          isLocked
        }
        commonTrackingProperties {
          atEventBillingState
          atEventBillingPlan
          atEventBillingCycle
          atEventBillingGateway
          atEventIsNewBuffer
          atEventChannelQty
        }
        dismissedObjects {
          id
        }
        name
        role
        billing {
          canAccessPublishing
          ... on OBBilling {
            canStartTrial
            subscription {
              interval
              plan {
                id
              }
            }
            changePlanOptions {
              planId
              planInterval
              isRecommended
            }
            gateway {
              gatewayType
            }
            paymentDetails {
              hasPaymentDetails
            }
          }
        }
        limits {
          ideas
          channels
        }
      }
    }
  }
`;
